import { IPivotItemProps } from '@fluentui/react';
import AppContext from 'App/AppContext';
import {
  IPDefaultButton,
  IPIconButton,
  IPPivotItem,
  IPPrimaryButton,
  IPText,
  IPToggle,
  IPTooltipHost,
} from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import ActivityTrail from 'components/Activity/ActivityTrail';
import AuditTrail from 'components/Audit/AuditTrail';
import { ChecklistMode } from 'components/Checklist/ChecklistContainer';
import DialogConfirmDelete from 'components/Dialogs/DialogConfirmDelete';
import DialogYesNo from 'components/Dialogs/DialogYesNo';
import TaskLinkList from 'components/Links/LinkLists/TaskLinkList';
import { globalStackTokensGapMedium, globalStackTokensGapSmall, globalTextStylesBold } from 'globalStyles';
import Joi from 'joi';
import Task from 'models/tasks/task';
import { TaskCheckList } from 'models/tasks/taskHelperClasses';
import TaskType from 'models/tasks/taskType';
import { Fragment, ReactNode, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetSingleTask } from 'services/Api/taskService';
import { apiRequest } from 'services/Auth/authConfig';
import { toDateOnly, toLocaleDateShort } from 'utils/datetime';
import { TaskContext } from '../Context/TaskContext';
import TaskTypeForm from '../Forms/TaskTypeForm';
import SingleTask, { ISingleTaskTypeProps } from '../SingleTask';
import { canUpdateTaskField, TaskFieldTypes } from '../TaskAuthHelper';
import TaskInstanceTab from '../TaskInstanceTab';
import TaskTopTasksBar from '../TaskTopTasksBar';
import { NormalTaskClassicLayout } from './NormalTaskClassicLayout';
import { NormalTaskDetailLayout } from './NormalTaskDetailLayout';
import { INormalTaskLayoutProps } from './NormalTaskLayoutProps';
import { getTaskPivotTabs } from './taskPivotTabs';
import { TaskModalMessages } from './TaskModalMessages';
import { ITaskDetailView } from './useTaskDetailView';
import TaskWorkflowDetailsModal from './TaskWorkflowDetailsModal';
import { useTaskPivotCounts } from './useTaskPivotCounts';
import { findTaskFieldError, getTaskValidationLabels } from './taskValidationHelpers';
import { useTemplateHasWorkflows } from './useTemplateHasWorkflows';

interface INormalTaskProps extends ISingleTaskTypeProps {
  onSaveAsNewTask: () => void;
  taskDetailView: ITaskDetailView;
}

// The shell of the normal task dialog: validation, the master task, the workflow messages, the dialogs and the
// save/cancel logic. The layout (classic or detail) only draws what the shell hands it.
const NormalTask = (props: INormalTaskProps) => {
  const { t } = useTranslation(['translation', 'task']);
  const appContext = useContext(AppContext);
  const [err, setErr] = useState<Joi.ValidationError | undefined>(undefined);
  const newEventHasWorkflows = useTemplateHasWorkflows(props.task, props.onError);
  const [isMasterTask, setIsMasterTask] = useState<Task | undefined>(undefined);
  const [showRemoveDialog, setShowRemoveDialog] = useState<boolean>(false);
  const [showCancelDialog, setShowCancelDialog] = useState<boolean>(false);
  const [showWorkflowDetails, setShowWorkflowDetails] = useState<boolean>(false);
  const [formOffersDocumentUpload, setFormOffersDocumentUpload] = useState<boolean | undefined>(undefined);
  //text typed in the checklist add row but not added yet: closing would lose it, so Cancel asks
  //first
  const [hasPendingNewItem, setHasPendingNewItem] = useState<boolean>(false);

  //the redesigned dialog; the user setting decides, a new task and the mobile view keep the classic
  //layout
  const { isNewView, rememberPivot, changeClassicView } = props.taskDetailView;
  const pivotTabs = getTaskPivotTabs(props.task, {
    isNewView,
    compactView: props.compactView,
    hasMasterTask: isMasterTask !== undefined,
    disallowSave: props.disallowSave,
    hasFormFeature: props.hasFormFeature,
  });
  const { pivotCounts, onActivityCountChange } = useTaskPivotCounts(props.task);

  const getStartDateRangeError = (): string | undefined => {
    if (!props.rescheduleDateRange) return undefined;
    if (props.task.startDateTime === props.orgTask.startDateTime) return undefined;

    if (
      props.rescheduleDateRange.start &&
      !props.rescheduleDateRange.end &&
      toDateOnly(props.task.startDateTime) <= toDateOnly(props.rescheduleDateRange.start)
    ) {
      return t('task:Start.Errors.After', { start: toLocaleDateShort(props.rescheduleDateRange.start) });
    }

    if (
      props.rescheduleDateRange.end &&
      !props.rescheduleDateRange.start &&
      toDateOnly(props.task.endDateTime) >= toDateOnly(props.rescheduleDateRange.end)
    ) {
      return t('task:Start.Errors.Before', { end: toLocaleDateShort(props.rescheduleDateRange.end) });
    }

    if (
      props.rescheduleDateRange.end &&
      props.rescheduleDateRange.start &&
      (toDateOnly(props.task.startDateTime) <= toDateOnly(props.rescheduleDateRange.start) ||
        toDateOnly(props.task.endDateTime) >= toDateOnly(props.rescheduleDateRange.end))
    ) {
      return t('task:Start.Errors.Between', {
        start: toLocaleDateShort(props.rescheduleDateRange.start),
        end: toLocaleDateShort(props.rescheduleDateRange.end),
      });
    }

    return undefined;
  };

  //
  // Effects
  //
  useEffect(() => {
    //the flag belongs to the loaded form: hide the upload button again until the new form has loaded
    setFormOffersDocumentUpload(undefined);
  }, [props.task.taskId, props.task.taskTypeId]);

  useEffect(() => {
    const result = props.task.validate(getTaskValidationLabels(t));
    setErr(result.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.task]);

  //
  // Helpers
  //
  const validate = (fieldName: string): string | undefined => {
    const fieldError = findTaskFieldError(err, fieldName);

    if (fieldError) {
      return fieldError;
    }

    if (fieldName === 'startDateTime') {
      return getStartDateRangeError();
    }

    return undefined;
  };

  const hasErrors = (): boolean => {
    return err || getStartDateRangeError() ? true : false;
  };

  const cannotSaveTask = (): boolean => {
    if (isMasterTask) {
      return hasErrors() || props.task.isEqual(props.orgTask);
    } else {
      return hasErrors() || (props.task.taskId !== -1 && props.task.isEqual(props.orgTask));
    }
  };

  const onCancel = () => {
    //queued activity messages of a new task are lost on cancel, so they count as unsaved changes
    if (cannotSaveTask() && !hasPendingNewItem && !props.pendingComments?.length) {
      props.onCancel();
    } else {
      setShowCancelDialog(true);
    }
  };

  const loadMasterTask = async () => {
    try {
      if (!props.task.taskMasterId) {
        return;
      }
      props.setIsActionPending(true);

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const tasks = await apiGetSingleTask(props.task.taskMasterId, false, accessToken, appContext.globalDataCache);

      if (tasks.tasks && tasks.tasks.length > 0) {
        const masterTask = tasks.tasks[0];

        setErr(undefined);
        setIsMasterTask(masterTask);
      } else {
        appContext.showNotification(t('translation:General.Notifications.NotAvailable'), true);
      }
    } catch (err) {
      appContext.setError(err);
    } finally {
      props.setIsActionPending(false);
    }
  };

  const onOpenMaster = () => {
    loadMasterTask();
  };

  const onUpdateCheckList = (newCheckList: TaskCheckList) => {
    const copyTask = props.task.clone();
    copyTask.checkList = newCheckList;
    props.onUpdateTask(copyTask);
  };

  const onClickForm = (taskTypeId: number) => {
    const newTask = props.task.clone();
    newTask.assignForm(taskTypeId > 0 ? appContext.globalDataCache.taskTypes.get(taskTypeId) : undefined);

    props.onUpdateTask(newTask);
    props.initPivot();
  };

  const onClickFinish = () => {
    const newTask = props.task.clone();
    newTask.setComplete();
    props.onUpdateTask(newTask);
    props.onSave(newTask, false);
  };

  const onClickArchive = () => {
    const newTask = props.task.clone();
    newTask.setArchived();
    props.onUpdateTask(newTask);
    props.onSave(newTask, false);
  };

  //
  // Render helpers
  //
  const getOutlookButton = (): JSX.Element | null => {
    if (!props.task.eventId && (props.task.isNonRecurring() || props.task.isSeries())) {
      //create
      return (
        <IPStack>
          <IPStack.Item>
            <IPPrimaryButton
              className="isoplanner-task-scheduleinoutlook"
              disabled={
                !props.task.isAssigned() ||
                hasErrors() ||
                props.isActionPending ||
                !canUpdateTaskField(props.task, props.canUpdate, TaskFieldTypes.OutlookCreate, appContext)
              }
              onClick={() => {
                props.createInOutlook();
              }}
            >
              {t('task:Outlook.CreateOutlook')}
            </IPPrimaryButton>
          </IPStack.Item>
        </IPStack>
      );
    } else if (props.task.eventId && (props.task.isNonRecurring() || props.task.isSeries())) {
      //remove
      return (
        <IPStack>
          <IPStack.Item>
            <IPPrimaryButton
              text={t('task:Outlook.RemoveOutlook')}
              disabled={
                hasErrors() ||
                props.isActionPending ||
                !canUpdateTaskField(props.task, props.canUpdate, TaskFieldTypes.OutlookRemove, appContext)
              }
              onClick={() => {
                props.removeFromOutlook();
              }}
            />
          </IPStack.Item>
        </IPStack>
      );
    }

    return null;
  };

  const onRenderTaskTypePivotItem = (
    itemProps?: IPivotItemProps,
    defaultRender?: (itemProps?: IPivotItemProps) => JSX.Element | null,
  ): JSX.Element | null => {
    //itemScope contains true when in compact view
    if (!itemProps || !itemProps.itemKey || !defaultRender) return null;

    //render default for compact mode
    if (props.compactView === true) {
      return defaultRender(itemProps);
    }

    return (
      <IPStack horizontal tokens={globalStackTokensGapSmall}>
        <IPStack.Item>
          <IPText styles={itemProps.itemKey === props.selectedPivot ? globalTextStylesBold : undefined}>
            {itemProps.headerText}
          </IPText>
        </IPStack.Item>
        <IPStack.Item>
          <IPTooltipHost content={t('forms:View.EnterFormMode')}>
            <IPIconButton iconProps={{ iconName: 'Fullscreen' }} onClick={() => props.setFormMode(true)} />
          </IPTooltipHost>
        </IPStack.Item>
      </IPStack>
    );
  };

  // One pivot tab by key; null when the task does not show that tab, so both layouts list the keys they use.
  const renderPivotItem = (key: string): JSX.Element | null => {
    switch (key) {
      case 'TaskDetails':
        return pivotTabs.taskDetails ? (
          <IPPivotItem
            className="isoplanner-task-pivot-details"
            key={key}
            itemKey={key}
            headerText={t('task:Pivot.TaskDetails')}
          />
        ) : null;
      case 'TaskType':
        return pivotTabs.taskType ? (
          <IPPivotItem
            className="isoplanner-task-pivot-tasktype"
            key={key}
            itemKey={key}
            headerText={TaskType.tabNameForTask(props.task.taskTypeId, props.task.formVersion, appContext)}
            onRenderItemLink={appContext.isMobileView ? undefined : onRenderTaskTypePivotItem}
          />
        ) : null;
      case 'Checklist':
        return pivotTabs.checklist ? (
          <IPPivotItem
            className="isoplanner-task-pivot-checklist"
            key={key}
            itemKey={key}
            headerText={t('task:Pivot.Checklist')}
            itemCount={pivotCounts.checklist}
          />
        ) : null;
      case 'Context':
        return (
          <IPPivotItem
            className="isoplanner-task-pivot-context"
            key={key}
            itemKey={key}
            headerText={t('task:Pivot.Context')}
            itemCount={pivotCounts.context}
          />
        );
      case 'Resources':
        return (
          <IPPivotItem
            className="isoplanner-task-pivot-library"
            key={key}
            itemKey={key}
            headerText={t('task:Pivot.Resources')}
            itemCount={pivotCounts.resources}
          />
        );
      case 'Activities':
        return pivotTabs.activities ? (
          <IPPivotItem
            className="isoplanner-task-pivot-activity"
            key={key}
            itemKey={key}
            headerText={t('task:Pivot.Activities')}
            itemCount={pivotCounts.activity}
          />
        ) : null;
      case 'Instances':
        return pivotTabs.instances ? (
          <IPPivotItem
            className="isoplanner-task-pivot-instances"
            key={key}
            itemKey={key}
            headerText={t('task:Pivot.Instances')}
          />
        ) : null;
      case 'Audit':
        return pivotTabs.audit ? (
          <IPPivotItem
            className="isoplanner-task-pivot-changelog"
            key={key}
            itemKey={key}
            headerText={t('task:Pivot.Audit')}
          />
        ) : null;
      default:
        return null;
    }
  };

  const renderTaskTypeForm = (isOpen: boolean, compactTaskView: boolean | undefined): JSX.Element => (
    <TaskTypeForm
      isOpen={isOpen}
      task={props.task}
      links={props.links}
      compact={true}
      compactTaskView={compactTaskView}
      //the saved state decides: picking another status must not unlock the form
      readonly={props.orgTask.isCompleted()}
      onUpdateData={props.onUpdateKPIData}
      onSetData={props.onSetKPIData}
      onUpdateTaskForForm={props.onUpdateTaskForForm}
      onFormLoaded={(elements) => setFormOffersDocumentUpload(elements.some((elm) => elm.offersDocumentUpload()))}
      addLinks={props.addLinkToTaskState}
      setLinks={props.setLinkToTaskState}
      removeLink={props.removeLinkFromTaskState}
      showErrors={props.showFormErrors}
      onValidate={props.onValidateForm}
      formMode={props.formMode}
      onExitFormMode={() => props.setFormMode(false)}
    ></TaskTypeForm>
  );

  const renderFooter = (leadingToggle?: ReactNode): JSX.Element => (
    <IPStack horizontal={!appContext.isMobileView} verticalAlign="end" tokens={globalStackTokensGapSmall}>
      <IPStack.Item>
        <IPStack horizontal tokens={globalStackTokensGapSmall}>
          <IPPrimaryButton
            disabled={cannotSaveTask() || props.isActionPending}
            onClick={() => {
              props.onSave(props.task, false);
            }}
          >
            {t('translation:General.Button.Save')}
          </IPPrimaryButton>
          <IPDefaultButton
            onClick={() => {
              onCancel();
            }}
          >
            {t('translation:General.Button.Cancel')}
          </IPDefaultButton>
        </IPStack>
      </IPStack.Item>
      <IPStack.Item grow>
        <IPStack horizontal horizontalAlign={appContext.isMobileView ? undefined : 'space-around'}>
          {getOutlookButton()}
        </IPStack>
      </IPStack.Item>
      {!appContext.isMobileView && (
        <IPStack.Item>
          <IPStack horizontal verticalAlign="center" tokens={globalStackTokensGapMedium}>
            {leadingToggle}
            {props.task.taskId !== -1 && (
              <IPToggle
                inlineLabel
                label={t('task:Detail.ClassicView')}
                checked={props.taskDetailView.viewMode === 'classic'}
                onChange={(_ev, checked) => changeClassicView(!!checked)}
                styles={{ root: { marginBottom: 0 } }}
              />
            )}
          </IPStack>
        </IPStack.Item>
      )}
    </IPStack>
  );

  const renderTopBar = (compact: boolean): JSX.Element => (
    <TaskTopTasksBar
      task={props.task}
      compact={compact}
      onClickTemplate={props.copyFromTemplate}
      onClickOpenMaster={props.task.isInstance() ? onOpenMaster : undefined}
      onClickFinish={onClickFinish}
      onClickArchive={onClickArchive}
      onClickFollowUp={!props.task.isSeries() ? props.onClickFollowUp : undefined}
      onClickRemove={() => setShowRemoveDialog(true)}
      onClickForm={props.task.isSeries() ? onClickForm : undefined}
      onCreateNewTaskTemplate={props.onCreateNewTaskTemplate}
      onSaveAsNewTask={props.onSaveAsNewTask}
      onConvertTaskType={props.onConvertTaskType}
      navigateExternal={props.navigateExternal}
      canUpdate={props.canUpdate}
      canDelete={props.canDelete}
      orgTask={props.orgTask}
      canSave={!hasErrors()}
      isLoading={props.isLoading}
      isActionPending={props.isActionPending}
      onUploadEvidence={props.onUploadEvidence}
      formOffersDocumentUpload={formOffersDocumentUpload}
      assistantAction={props.assistantAction}
      onOpenAssistant={props.onOpenAssistant}
      onInstancesFormUpgraded={props.onSeriesFormUpgraded}
    />
  );

  const renderMessages = (onShowContext: () => void): JSX.Element => (
    <TaskModalMessages
      task={props.task}
      error={props.error}
      onAfterErrorAction={props.onAfterErrorAction}
      newEventHasWorkflows={newEventHasWorkflows}
      onShowWorkflowDetails={() => setShowWorkflowDetails(true)}
      onShowContext={onShowContext}
    />
  );

  const renderPivotPanels = (selectedPivot: string | undefined): JSX.Element => (
    <Fragment>
      {selectedPivot === 'Resources' && (
        <TaskLinkList
          taskLinks={props.links}
          task={props.task}
          addLinks={props.addLinkToTaskState}
          setLinks={props.setLinkToTaskState}
          removeLink={props.removeLinkFromTaskState}
          navigateExternal={props.navigateExternal}
        />
      )}
      {selectedPivot === 'Activities' && (
        <IPStack.Item grow>
          <ActivityTrail
            auditTrailId={props.task.auditTrailId}
            commentTrailId={props.task.commentTrailId}
            taskId={props.task.taskId}
            authSchemaIds={[props.task.authSchemaId]}
            onCommentCountChange={onActivityCountChange}
            onError={props.onError}
            pendingComments={props.pendingComments}
            onQueueComment={props.onQueueComment}
            onRemovePendingComment={props.onRemovePendingComment}
          />
        </IPStack.Item>
      )}
      {selectedPivot === 'Instances' && (
        <IPStack.Item grow>
          <TaskInstanceTab
            taskId={
              props.task.isInstance()
                ? props.task.taskMasterId
                  ? props.task.taskMasterId
                  : props.task.taskId
                : props.task.taskId
            }
            activeTaskId={props.task.isInstance() ? props.task.taskId : undefined}
            windowLevel={props.windowLevel}
            navigateExternal={props.navigateExternal}
            reloadToken={props.instancesReloadToken}
          />
        </IPStack.Item>
      )}
      {selectedPivot === 'Audit' && (
        <IPStack.Item grow>
          <AuditTrail auditTrailId={props.task.auditTrailId} />
        </IPStack.Item>
      )}
      {selectedPivot === 'Context' && (
        <IPStack.Item grow>
          <TaskContext
            orgTask={props.orgTask}
            task={props.task}
            updateTaskForForm={props.onUpdateTaskForForm}
            updateTask={props.onUpdateTask}
            onClickFollowUp={!props.task.isSeries() ? props.onClickFollowUp : undefined}
            onLinkFollowUpToTemplate={!props.task.isSeries() ? props.onLinkFollowUpToTemplate : undefined}
            onCreateEmptyFollowUpTask={!props.task.isSeries() ? props.onCreateEmptyFollowUpTask : undefined}
            allowEdit={canUpdateTaskField(props.orgTask, props.canUpdate, TaskFieldTypes.Context, appContext)}
            windowLevel={props.windowLevel}
            navigateExternal={true}
            narrow={isNewView}
          />
        </IPStack.Item>
      )}
    </Fragment>
  );

  const layoutProps: INormalTaskLayoutProps = {
    taskProps: props,
    pivotTabs,
    checklistProps: {
      task: props.task,
      orgTask: props.orgTask,
      onUpdateCheckList: onUpdateCheckList,
      mode: props.task.isSeries() ? ChecklistMode.Creator : ChecklistMode.User,
      canUpdate: props.canUpdate,
    },
    validate,
    onCancel,
    renderMessages,
    renderTopBar,
    renderFooter,
    renderTaskTypeForm,
    renderPivotItem,
    renderPivotPanels,
    children: (
      <Fragment>
        <DialogConfirmDelete
          onNo={() => {
            setShowRemoveDialog(false);
          }}
          onYes={() => {
            setShowRemoveDialog(false);
            props.onRemove();
          }}
          title={props.task.isSeries() ? t('task:RemoveSeries.DialogTitle') : t('task:Remove.DialogTitle')}
          subText={props.task.isSeries() ? t('task:RemoveSeries.DialogSubTitle') : t('task:Remove.DialogSubTitle')}
          hidden={!showRemoveDialog}
          confirmText={!props.task.isSeries() ? t('task:Remove.RemoveDialogConfirmText') : undefined}
        />
        <DialogYesNo
          onNo={() => {
            setShowCancelDialog(false);
          }}
          onYes={() => {
            setShowCancelDialog(false);
            props.onCancel();
          }}
          title={t('task:Cancel.DialogTitle')}
          subText={t('task:Cancel.DialogSubTitle')}
          hidden={!showCancelDialog}
        />
        {isMasterTask && (
          <SingleTask
            isOpen={isMasterTask !== undefined}
            task={isMasterTask}
            windowLevel={(props.windowLevel ?? 0) + 1}
            close={() => setIsMasterTask(undefined)}
            onSave={() => setIsMasterTask(undefined)}
            onUpdate={() => {}}
            onRemove={() => props.onRemove()}
          />
        )}
        <TaskWorkflowDetailsModal
          hidden={!showWorkflowDetails}
          task={props.task}
          onClose={() => setShowWorkflowDetails(false)}
        />
      </Fragment>
    ),
  };

  //
  // Main render
  //
  return isNewView ? (
    <NormalTaskDetailLayout
      {...layoutProps}
      rememberPivot={rememberPivot}
      onPendingNewItemChange={setHasPendingNewItem}
    />
  ) : (
    <NormalTaskClassicLayout {...layoutProps} />
  );
};

export default NormalTask;
